// components/EditContractModal.js
import React, { useState, useEffect } from 'react';
import {
  FaEdit,
  FaTimes,
  FaSave,
  FaCalendarAlt,
  FaPercentage,
  FaFileContract,
  FaUser,
  FaPhone,
  FaEnvelope,
  FaMoneyBillWave,
  FaFileSignature,
} from 'react-icons/fa';
import { toast } from 'react-toastify';

const EditContractModal = ({ contract, isOpen, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    contractname: '',
    contractStartDate: '',
    contractEndDate: '',
    gst_number: '',
    autoRenewal: false,
    renewalNoticePeriod: 30,
    termsAndConditions: '',
    contactDetails: {
      contactPerson: '',
      contactEmail: '',
      contactPhone: '',
    },
    signatoryDetails: {
      signatoryName: '',
      signatoryTitle: '',
      signatoryEmail: '',
      signatoryPhone: '',
    },
    discountStructure: {
      discountType: 'flat',
      flatDiscount: 0,
      inclusiveDiscount: true,
      discountSlabs: [],
      nonApplicableConditions: [],
      nonInclusiveItems: [],
    },
    paymentTerms: {
      billingCycle: 'monthly',
      paymentTerms: 'net30',
      latePaymentFee: 0,
      creditlimit: 0,
      baseprice: 0,
      currency: 'INR',
      invoicingDate: '1',
      paymentDueDays: 7,
    },
    withdrawalTerms: {
      withdrawalNotice: '30',
      withdrawalPenalty: '',
      forceMajeure: '',
      penaltyApplicable: false,
    },
  });

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  // Initialize form data when contract changes
  useEffect(() => {
    if (contract && isOpen) {
      setFormData({
        contractname: contract.contractname || '',
        contractStartDate: contract.contractStartDate
          ? contract.contractStartDate.split('T')[0]
          : '',
        contractEndDate: contract.contractEndDate ? contract.contractEndDate.split('T')[0] : '',
        gst_number: contract.gst_number || '',
        autoRenewal: contract.autoRenewal || false,
        renewalNoticePeriod: contract.renewalNoticePeriod || 30,
        termsAndConditions: contract.termsAndConditions || '',
        contactDetails: {
          contactPerson: contract.contactDetails?.contactPerson || '',
          contactEmail: contract.contactDetails?.contactEmail || '',
          contactPhone: contract.contactDetails?.contactPhone || '',
        },
        signatoryDetails: {
          signatoryName: contract.signatoryDetails?.signatoryName || '',
          signatoryTitle: contract.signatoryDetails?.signatoryTitle || '',
          signatoryEmail: contract.signatoryDetails?.signatoryEmail || '',
          signatoryPhone: contract.signatoryDetails?.signatoryPhone || '',
        },
        discountStructure: {
          discountType: contract.discountStructure?.discountType || 'flat',
          flatDiscount: contract.discountStructure?.flatDiscount || 0,
          inclusiveDiscount:
            contract.discountStructure?.inclusiveDiscount !== undefined
              ? contract.discountStructure.inclusiveDiscount
              : true,
          discountSlabs: contract.discountStructure?.discountSlabs || [],
          nonApplicableConditions: contract.discountStructure?.nonApplicableConditions || [],
          nonInclusiveItems: contract.discountStructure?.nonInclusiveItems || [],
        },
        paymentTerms: {
          billingCycle: contract.paymentTerms?.billingCycle || 'monthly',
          paymentTerms: contract.paymentTerms?.paymentTerms || 'net30',
          latePaymentFee: contract.paymentTerms?.latePaymentFee || 0,
          creditlimit: contract.paymentTerms?.creditlimit || 0,
          baseprice: contract.paymentTerms?.baseprice || 0,
          currency: contract.paymentTerms?.currency || 'INR',
          invoicingDate: contract.paymentTerms?.invoicingDate || '1',
          paymentDueDays: contract.paymentTerms?.paymentDueDays || 7,
        },
        withdrawalTerms: {
          withdrawalNotice: contract.withdrawalTerms?.withdrawalNotice || '30',
          withdrawalPenalty: contract.withdrawalTerms?.withdrawalPenalty || '',
          forceMajeure: contract.withdrawalTerms?.forceMajeure || '',
          penaltyApplicable: contract.withdrawalTerms?.penaltyApplicable || false,
        },
      });
    }
  }, [contract, isOpen]);

  const handleInputChange = (section, field, value) => {
    if (section) {
      setFormData((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Prepare data for API
      const updateData = {
        contractname: formData.contractname,
        contractStartDate: `${formData.contractStartDate}T00:00:00.000Z`,
        contractEndDate: `${formData.contractEndDate}T23:59:59.999Z`,
        gst_number: formData.gst_number,
        autoRenewal: formData.autoRenewal,
        renewalNoticePeriod: formData.renewalNoticePeriod,
        termsAndConditions: formData.termsAndConditions,
        contactDetails: formData.contactDetails,
        signatoryDetails: formData.signatoryDetails,
        discountStructure: formData.discountStructure,
        paymentTerms: formData.paymentTerms,
        withdrawalTerms: formData.withdrawalTerms,
      };

      // Remove empty fields to avoid validation errors
      Object.keys(updateData).forEach((key) => {
        if (updateData[key] === '' || updateData[key] === null || updateData[key] === undefined) {
          delete updateData[key];
        }
      });

      await onUpdate(contract.contractid, updateData);
      onClose();
    } catch (error) {
      console.error('Error updating contract:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: FaFileContract },
    { id: 'contact', label: 'Contact Details', icon: FaUser },
    { id: 'financial', label: 'Financial', icon: FaMoneyBillWave },
    { id: 'terms', label: 'Terms & Conditions', icon: FaFileSignature },
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <FaEdit className="text-black text-xl" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-black">Edit Contract</h2>
              <p className="text-gray-600">
                Update contract details for {contract?.company?.companyname}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition duration-200"
          >
            <FaTimes className="text-gray-500 text-xl" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex space-x-1 px-6">
            {tabs.map((tab) => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-3 rounded-t-lg font-medium transition duration-200 ${
                    activeTab === tab.id
                      ? 'bg-black text-white'
                      : 'text-gray-600 hover:text-black hover:bg-gray-100'
                  }`}
                >
                  <IconComponent className="text-sm" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Form Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information Tab */}
            {activeTab === 'basic' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contract Name *
                  </label>
                  <input
                    type="text"
                    value={formData.contractname}
                    onChange={(e) => handleInputChange(null, 'contractname', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">GST Number</label>
                  <input
                    type="text"
                    value={formData.gst_number}
                    onChange={(e) => handleInputChange(null, 'gst_number', e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg"
                    placeholder="22AAAAA0000A1Z5"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date *
                  </label>
                  <div className="relative">
                    <FaCalendarAlt className="absolute left-3 top-3.5 text-gray-400" />
                    <input
                      type="date"
                      value={formData.contractStartDate}
                      onChange={(e) => handleInputChange(null, 'contractStartDate', e.target.value)}
                      className="w-full pl-10 p-3 border border-gray-300 rounded-lg"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Date *</label>
                  <div className="relative">
                    <FaCalendarAlt className="absolute left-3 top-3.5 text-gray-400" />
                    <input
                      type="date"
                      value={formData.contractEndDate}
                      onChange={(e) => handleInputChange(null, 'contractEndDate', e.target.value)}
                      className="w-full pl-10 p-3 border border-gray-300 rounded-lg"
                      required
                    />
                  </div>
                </div>

                <div className="lg:col-span-2">
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.autoRenewal}
                        onChange={(e) => handleInputChange(null, 'autoRenewal', e.target.checked)}
                        className="rounded border-gray-300 text-black"
                      />
                      <span className="text-sm font-medium text-gray-700">Auto Renewal</span>
                    </label>

                    {formData.autoRenewal && (
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-600">Notice Period:</span>
                        <input
                          type="number"
                          value={formData.renewalNoticePeriod}
                          onChange={(e) =>
                            handleInputChange(null, 'renewalNoticePeriod', parseInt(e.target.value))
                          }
                          className="w-20 p-2 border border-gray-300 rounded-lg"
                          min="1"
                          max="90"
                        />
                        <span className="text-sm text-gray-600">days</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Contact Details Tab */}
            {activeTab === 'contact' && (
              <div className="space-y-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-black mb-4 flex items-center space-x-2">
                    <FaUser className="text-black" />
                    <span>Primary Contact Details</span>
                  </h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Contact Person *
                      </label>
                      <input
                        type="text"
                        value={formData.contactDetails.contactPerson}
                        onChange={(e) =>
                          handleInputChange('contactDetails', 'contactPerson', e.target.value)
                        }
                        className="w-full p-3 border border-gray-300 rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Contact Email *
                      </label>
                      <div className="relative">
                        <FaEnvelope className="absolute left-3 top-3.5 text-gray-400" />
                        <input
                          type="email"
                          value={formData.contactDetails.contactEmail}
                          onChange={(e) =>
                            handleInputChange('contactDetails', 'contactEmail', e.target.value)
                          }
                          className="w-full pl-10 p-3 border border-gray-300 rounded-lg"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Contact Phone
                      </label>
                      <div className="relative">
                        <FaPhone className="absolute left-3 top-3.5 text-gray-400" />
                        <input
                          type="tel"
                          value={formData.contactDetails.contactPhone}
                          onChange={(e) =>
                            handleInputChange('contactDetails', 'contactPhone', e.target.value)
                          }
                          className="w-full pl-10 p-3 border border-gray-300 rounded-lg"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-black mb-4 flex items-center space-x-2">
                    <FaFileSignature className="text-black" />
                    <span>Signatory Details</span>
                  </h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Signatory Name *
                      </label>
                      <input
                        type="text"
                        value={formData.signatoryDetails.signatoryName}
                        onChange={(e) =>
                          handleInputChange('signatoryDetails', 'signatoryName', e.target.value)
                        }
                        className="w-full p-3 border border-gray-300 rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Signatory Title *
                      </label>
                      <input
                        type="text"
                        value={formData.signatoryDetails.signatoryTitle}
                        onChange={(e) =>
                          handleInputChange('signatoryDetails', 'signatoryTitle', e.target.value)
                        }
                        className="w-full p-3 border border-gray-300 rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Signatory Email *
                      </label>
                      <div className="relative">
                        <FaEnvelope className="absolute left-3 top-3.5 text-gray-400" />
                        <input
                          type="email"
                          value={formData.signatoryDetails.signatoryEmail}
                          onChange={(e) =>
                            handleInputChange('signatoryDetails', 'signatoryEmail', e.target.value)
                          }
                          className="w-full pl-10 p-3 border border-gray-300 rounded-lg"
                          required
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Signatory Phone
                      </label>
                      <div className="relative">
                        <FaPhone className="absolute left-3 top-3.5 text-gray-400" />
                        <input
                          type="tel"
                          value={formData.signatoryDetails.signatoryPhone}
                          onChange={(e) =>
                            handleInputChange('signatoryDetails', 'signatoryPhone', e.target.value)
                          }
                          className="w-full pl-10 p-3 border border-gray-300 rounded-lg"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Financial Tab */}
            {activeTab === 'financial' && (
              <div className="space-y-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-black mb-4 flex items-center space-x-2">
                    <FaPercentage className="text-black" />
                    <span>Discount Structure</span>
                  </h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Discount Type
                      </label>
                      <select
                        value={formData.discountStructure.discountType}
                        onChange={(e) =>
                          handleInputChange('discountStructure', 'discountType', e.target.value)
                        }
                        className="w-full p-3 border border-gray-300 rounded-lg"
                      >
                        <option value="flat">Flat Discount</option>
                        <option value="slab">Slab-based Discount</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Flat Discount (%)
                      </label>
                      <div className="relative">
                        <FaPercentage className="absolute left-3 top-3.5 text-gray-400" />
                        <input
                          type="number"
                          value={formData.discountStructure.flatDiscount}
                          onChange={(e) =>
                            handleInputChange(
                              'discountStructure',
                              'flatDiscount',
                              parseFloat(e.target.value)
                            )
                          }
                          className="w-full pl-10 p-3 border border-gray-300 rounded-lg"
                          step="0.01"
                          min="0"
                          max="100"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.discountStructure.inclusiveDiscount}
                        onChange={(e) =>
                          handleInputChange(
                            'discountStructure',
                            'inclusiveDiscount',
                            e.target.checked
                          )
                        }
                        className="rounded border-gray-300 text-black"
                      />
                      <span className="text-sm font-medium text-gray-700">Inclusive Discount</span>
                    </label>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-black mb-4 flex items-center space-x-2">
                    <FaMoneyBillWave className="text-black" />
                    <span>Payment Terms</span>
                  </h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Billing Cycle
                      </label>
                      <select
                        value={formData.paymentTerms.billingCycle}
                        onChange={(e) =>
                          handleInputChange('paymentTerms', 'billingCycle', e.target.value)
                        }
                        className="w-full p-3 border border-gray-300 rounded-lg"
                      >
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="annual">Annual</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Payment Terms
                      </label>
                      <select
                        value={formData.paymentTerms.paymentTerms}
                        onChange={(e) =>
                          handleInputChange('paymentTerms', 'paymentTerms', e.target.value)
                        }
                        className="w-full p-3 border border-gray-300 rounded-lg"
                      >
                        <option value="net30">Net 30</option>
                        <option value="net15">Net 15</option>
                        <option value="net45">Net 45</option>
                        <option value="net60">Net 60</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Late Payment Fee
                      </label>
                      <input
                        type="number"
                        value={formData.paymentTerms.latePaymentFee}
                        onChange={(e) =>
                          handleInputChange(
                            'paymentTerms',
                            'latePaymentFee',
                            parseFloat(e.target.value)
                          )
                        }
                        className="w-full p-3 border border-gray-300 rounded-lg"
                        step="0.01"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Credit Limit
                      </label>
                      <input
                        type="number"
                        value={formData.paymentTerms.creditlimit}
                        onChange={(e) =>
                          handleInputChange(
                            'paymentTerms',
                            'creditlimit',
                            parseFloat(e.target.value)
                          )
                        }
                        className="w-full p-3 border border-gray-300 rounded-lg"
                        step="0.01"
                        min="0"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Terms & Conditions Tab */}
            {activeTab === 'terms' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Terms & Conditions *
                  </label>
                  <textarea
                    value={formData.termsAndConditions}
                    onChange={(e) => handleInputChange(null, 'termsAndConditions', e.target.value)}
                    rows="12"
                    className="w-full p-3 border border-gray-300 rounded-lg resize-none"
                    placeholder="Enter detailed terms and conditions for the contract..."
                    required
                  />
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-semibold text-black mb-4">Withdrawal Terms</h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Withdrawal Notice (days)
                      </label>
                      <input
                        type="text"
                        value={formData.withdrawalTerms.withdrawalNotice}
                        onChange={(e) =>
                          handleInputChange('withdrawalTerms', 'withdrawalNotice', e.target.value)
                        }
                        className="w-full p-3 border border-gray-300 rounded-lg"
                        placeholder="30"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Withdrawal Penalty
                      </label>
                      <input
                        type="text"
                        value={formData.withdrawalTerms.withdrawalPenalty}
                        onChange={(e) =>
                          handleInputChange('withdrawalTerms', 'withdrawalPenalty', e.target.value)
                        }
                        className="w-full p-3 border border-gray-300 rounded-lg"
                        placeholder="10% of contract value"
                      />
                    </div>
                    <div className="lg:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Force Majeure Clause
                      </label>
                      <textarea
                        value={formData.withdrawalTerms.forceMajeure}
                        onChange={(e) =>
                          handleInputChange('withdrawalTerms', 'forceMajeure', e.target.value)
                        }
                        rows="3"
                        className="w-full p-3 border border-gray-300 rounded-lg"
                        placeholder="Terms and conditions for force majeure events..."
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-4 p-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition duration-200 font-medium"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition duration-200 font-medium flex items-center space-x-2 disabled:opacity-50"
          >
            <FaSave className="text-sm" />
            <span>{loading ? 'Updating...' : 'Update Contract'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditContractModal;
